import { useMemo, useState } from "react";

import {
  Button,
  Icon_ChevronUpDown24,
  Icon_Kebab,
  Menu,
  useModalManager,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import sidebarIcon from "../../assets/images/sidebar-icon.svg";
import { defaultPrivateSpaceName, defaultTeamSpaceName, webAppURL } from "../../config";
import { ModalIds } from "../../constants/modalIds";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useProject } from "../../hooks/useProject";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import { ProjectRoleUtils } from "../../types";
import { formatDate } from "../../utils/helpers";
import { DeepLinkModal } from "../DeepLinkModal/DeepLinkModal";
import { OverlayProvider } from "../Headers/components/ShareModal/components/MenuPortal";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";
import { UnpublishConfirmationModal } from "../UnpublishConfirmationModal/UnpublishConfirmationModal";
import { FloatingBreadcrumbs } from "./FloatingBreadcrumbs/FloatingBreadcrumbs";
import { ProjectMetadata } from "./ProjectMetadata/ProjectMetadata";

import style from "./ProjectSidebar.module.css";

const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

interface ProjectSidebarProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  isCommentingEnabled: boolean;
}

export const ProjectSidebar = ({ isOpen, setOpen, isCommentingEnabled }: ProjectSidebarProps) => {
  const { data: accountData } = useAccount();
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData } = useWebExport();
  const { viewLinkId = "", projectId = "", drawingId = "" } = useParams();

  const navigation = useNavigation();
  const tracker = useTracker();

  const [isUnpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const { open: openModal } = useModalManager();

  const openShareModal = () => openModal({ id: ModalIds.SHARE_VIEW_LINK });

  const userName = useMemo(() => {
    const user = projectData?.users?.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );
    if (user && user.userId === accountData?.id) {
      return user.name ? `${user.name}` : "You";
    }
    return user?.name;
  }, [projectData, accountData]);

  const spaceName =
    projectData?.space?.spaceName ??
    spaceTypeNameMap[projectData?.space?.spaceType ?? ""] ??
    undefined;

  const userRole = projectData?.userRole ?? webExportData?.userRole;

  const handleCopyLink = () => {
    // Currently only one view link per web export is supported, so we take the first one
    const url = `${webAppURL}/p/${webExportData?.webExport.projectId}/v/${
      projectData?.projectViewLinks?.[0]?.projectViewLinkId ?? ""
    }`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success("Copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy link to clipboard");
      });
  };

  return (
    <div className={classNames([style.sidebarWrapper, !isOpen && style.closed])}>
      <div className={style.sidebar}>
        {webExportData && (
          <>
            <ProjectMetadata
              isLoading={isProjectLoading}
              teamId={projectData?.teamId}
              spaceName={spaceName}
              projectName={projectData?.project.name ?? webExportData.webExport.title}
              lastModifiedAt={
                projectData?.latestRevision.lastModifiedAt ?? webExportData.webExport.createdAt
              }
              userName={userName}
              isCurrentUser={projectData?.latestRevision.uploadedByUserId === accountData?.id}
              showEditInAppButton={userRole === "owner" || userRole === "editor"}
            />
            <div className={style.divider}></div>
          </>
        )}

        {webExportData && (
          <OverlayProvider>
            <div className={style.versionsWrapper}>
              <div className={style.currentVersion}>
                <div className={style.icon}>
                  <Button variant="borderless" disabled>
                    <Icon_ChevronUpDown24 style={{ height: "16px", width: "16px" }} />
                  </Button>
                </div>
                <div className={style.data}>
                  <div className={style.title}>
                    {formatDate(webExportData?.webExport.createdAt)}
                  </div>
                  {projectData && <div className={style.subtitle}>Published by {userName}</div>}
                  <div className={style.subtitle}>
                    #
                    <span>
                      {projectData?.latestRevision.revisionId ?? webExportData.webExport.revisionId}
                    </span>
                  </div>
                </div>
                {projectData && (
                  <div className={style.actions}>
                    <Menu
                      activator={
                        <Button variant="borderless" aria-label="More options">
                          <Icon_Kebab />
                        </Button>
                      }
                      className={style.contextMenu}
                      onOpen={() => {
                        tracker.trackContextMenuOpened({
                          location: "project_page_version_selector",
                        });
                      }}
                    >
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackContextMenuActed({
                            action: "copy_link",
                            location: "project_page_version_selector",
                          });
                          handleCopyLink();
                        }}
                      >
                        <span>Copy Link</span>
                      </Menu.Item>

                      {userRole && ProjectRoleUtils.isAtLeast(userRole, "editor") && (
                        <>
                          <Menu.Item
                            closeOnClick
                            onClick={() => {
                              tracker.trackContextMenuActed({
                                action: "share",
                                location: "project_page_version_selector",
                              });
                              openShareModal();
                            }}
                          >
                            <span>Share...</span>
                          </Menu.Item>

                          <Menu.Item
                            closeOnClick
                            onClick={() => {
                              tracker.trackContextMenuActed({
                                action: "unpublish",
                                location: "project_page_version_selector",
                              });
                              setUnpublishModalOpen(true);
                            }}
                          >
                            <span className={style.unpublish}>Unpublish</span>
                          </Menu.Item>
                        </>
                      )}
                    </Menu>
                  </div>
                )}
              </div>

              <div className={style.modelList}>
                <button
                  className={classNames([style.listItem, !drawingId && style.active])}
                  onClick={() =>
                    navigation.goToViewLink({
                      viewLinkId,
                      projectId,
                    })
                  }
                >
                  {(projectData?.latestRevision.thumbnailDownloads?.light?.url ||
                    webExportData.revision?.thumbnailDownloads?.light?.url) && (
                    <div className={style.listItemIcon}>
                      <img
                        src={
                          projectData?.latestRevision.thumbnailDownloads?.light?.url ??
                          webExportData.revision?.thumbnailDownloads?.light?.url
                        }
                        alt="Model Thumbnail"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                  )}

                  <div className={style.listItemTitle}>Model</div>
                </button>
              </div>
              {webExportData.webExport.drawingsDownload?.length && (
                <div className={style.drawingsList}>
                  <div className={style.drawingsTitle}>Drawings</div>

                  {webExportData.webExport.drawingsDownload.map((drawing) => (
                    <button
                      key={drawing.drawingId}
                      className={classNames([
                        style.listItem,
                        drawing.drawingId === drawingId ? style.active : "",
                      ])}
                      onClick={() =>
                        navigation.goToDrawing({
                          viewLinkId,
                          projectId,
                          drawingId: drawing.drawingId,
                        })
                      }
                    >
                      <div className={style.listItemTitle}>{drawing.title}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </OverlayProvider>
        )}
      </div>
      <div className={style.controlButton}>
        <ToolButton
          alt="Project Outline"
          title="Project Outline"
          icon={sidebarIcon}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          isActive={isOpen}
        />
        {!drawingId && isCommentingEnabled && <Toolbar />}

        {projectData?.teamId && spaceName && (
          <FloatingBreadcrumbs
            teamId={projectData?.teamId}
            spaceName={spaceName}
            isClosed={isOpen}
          />
        )}
      </div>
      <UnpublishConfirmationModal
        open={isUnpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onUnpublishSuccess={() => {
          navigation.goToProject({ projectId: projectData?.project.projectId ?? "" });
        }}
        viewLinkId={viewLinkId}
        telemetryLocation={"review_links"} // TODO: fix telemetry location
      />
      <DeepLinkModal />
    </div>
  );
};
